import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext } from '../../App';
import CCContext, { isCCPage, supportsCCOverlay } from '../../contexts/CCContext';
import CC from '../../services/CCPublicationService';
import { session } from '../../utilities/session';
import { CC_SELECTION_KEY, countryForRegion } from './contract';
import useCCResource from './useCCResource';
import { createGeography } from './CCGeography';
import { normalizeExclusions } from './cropExclusions';
import YieldProvider from './YieldContext';

export function readCCIdentity() {
  try {
    // These claims partition local state only. All permissions come from the API.
    const raw = localStorage.getItem('token');
    const encoded = raw?.split('.')[1];
    const claims = encoded ? JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/'))) : {};
    const bundle = JSON.parse(localStorage.getItem(session.key) || 'null');
    return JSON.stringify([claims.sub || null, Object.keys(claims.organization || {}).sort(),
      bundle?.id?.startsWith('legacy:') ? 'legacy' : bundle?.id || null]);
  } catch (_) { return 'anonymous'; }
}
function readSaved() {
  try { return JSON.parse(localStorage.getItem(CC_SELECTION_KEY)) || {}; } catch (_) { return {}; }
}
export default function CCV2Provider({ children, setTransport }) {
  const { pathname } = useLocation();
  const global = useContext(GlobalContext);
  const active = isCCPage(pathname) || (supportsCCOverlay(pathname) && global.globalCCViewEnabled && !global.globalFieldDrawUIEnabled);
  const [identity, setIdentity] = useState(readCCIdentity);
  const [capabilities, setCapabilities] = useState({ status: 'idle' });
  const [refresh, setRefresh] = useState(0);
  const [saved] = useState(readSaved);
  const [country, setCountry] = useState(saved.country || countryForRegion(global.globalSelectedRegion));
  const [selection, setSelection] = useState(null);
  const [modeState, setModeState] = useState(null);
  const [scopeState, setScopeState] = useState(null);
  const [path, setPath] = useState([]);
  const [classCode, setClassCode] = useState(null);
  const [exclusionState, setExclusionState] = useState(null);
  const [comparisonIds, setComparisonIds] = useState([]);
  const [legendScope, setLegendScope] = useState('region');
  const [polygon, setPolygon] = useState(null);
  const [navigationState, setNavigationState] = useState({});
  const [sidebarRecord, setSidebarRecord] = useState(null);
  const [statsRefresh, setStatsRefresh] = useState(0);
  const [validationRefresh, setValidationRefresh] = useState(0);
  const retryStats = useCallback(() => setStatsRefresh((value) => value + 1), []);
  const retryValidation = useCallback(() => setValidationRefresh((value) => value + 1), []);
  const [mapMounted, setMapMounted] = useState(false);
  const [mapLoad, setMapLoad] = useState(null);
  const currentMapKey = useRef(null);
  const registerMap = useCallback(() => {
    setMapMounted(true);
    return () => { setMapMounted(false); setMapLoad(null); };
  }, []);
  const reportMapLoading = useCallback((key, loading) => {
    if (key !== currentMapKey.current) return;
    setMapLoad((old) => old?.key === key && old.loading === loading ? old : { key, loading });
  }, []);
  const navigationRequest = useRef(null);
  const retry = useCallback(() => setRefresh((value) => value + 1), []);

  useEffect(() => {
    if (!active) return;
    const inspect = () => setIdentity(readCCIdentity());
    const timer = setInterval(inspect, 1000);
    window.addEventListener('storage', inspect);
    window.addEventListener('focus', inspect);
    return () => { clearInterval(timer); window.removeEventListener('storage', inspect); window.removeEventListener('focus', inspect); };
  }, [active]);
  useEffect(() => {
    if (!active) return;
    let controller; let disposed = false;
    const load = async () => {
      controller?.abort(); controller = new AbortController();
      const signal = controller.signal;
      try {
        const data = await CC.capabilities(signal);
        if (!disposed && !signal.aborted && identity === readCCIdentity()) setCapabilities({ identity, status: 'ready', data });
      } catch (error) {
        if (!disposed && !signal.aborted) setCapabilities({ identity, status: 'error', error });
      }
    };
    const invalidate = () => { setCapabilities({ identity, status: 'loading' }); load(); };
    load();
    const timer = setInterval(load, 30000);
    window.addEventListener('focus', load);
    window.addEventListener('cc-access-invalidated', invalidate);
    return () => { disposed = true; controller?.abort(); clearInterval(timer); window.removeEventListener('focus', load); window.removeEventListener('cc-access-invalidated', invalidate); };
  }, [active, identity, refresh]);
  const permission = active && capabilities.identity === identity && capabilities.status === 'ready' ? capabilities.data : null;
  const owner = permission ? `${identity}:${permission.organization_id}` : null;
  const accessKey = owner ? `${owner}:${permission.access_revision}` : null;
  const can = (value) => !!permission?.capabilities.includes(value);
  const mode = modeState?.key === accessKey ? modeState.value : can('explore') ? 'explore'
    : can('view_my_territory') ? 'me' : can('view_commercial_regions') ? 'commercial' : 'manage';
  useEffect(() => { setClassCode(null); setComparisonIds([]); setPolygon(null); }, [accessKey, country]);
  const catalogKey = accessKey && /^[A-Z]{2}$/.test(country) ? JSON.stringify([accessKey, country, refresh]) : null;
  const publications = useCCResource(catalogKey, (signal) => CC.catalog(country, signal));
  const available = publications.data?.publications || [];
  const selectedId = selection?.owner === owner && selection.country === country ? selection.id
    : saved.country === country && (!saved.version || saved.owner === owner) ? saved.snapshot_id || saved.id
      : publications.data?.default_snapshot_id;
  const snapshot = available.find((item) => item.snapshot_id === selectedId) || null;
  const exclusionsEnabled = permission?.features?.crop_exclusions === true;
  const exclusionOwner = accessKey ? JSON.stringify([accessKey, country]) : null;
  const excludedClassKeys = exclusionsEnabled && exclusionOwner && exclusionState?.key === exclusionOwner ? exclusionState.keys : [];
  const toggleCropExclusion = (code) => {
    const entry = snapshot?.classes.find((crop) => crop.code === code);
    if (!exclusionsEnabled || !entry || !snapshot.present_class_codes.includes(code)) return;
    setExclusionState((old) => {
      const keys = old?.key === exclusionOwner ? old.keys : [];
      return { key: exclusionOwner, keys: keys.includes(entry.semantic_key)
        ? keys.filter((key) => key !== entry.semantic_key) : normalizeExclusions([...keys, entry.semantic_key]) };
    });
    setClassCode(null);
    setPolygon(null);
  };
  const clearCropExclusions = () => { setExclusionState(null); setClassCode(null); setPolygon(null); };
  const scopeKey = snapshot ? `${accessKey}:${snapshot.snapshot_id}:${mode}` : null;
  const geographyKey = accessKey ? JSON.stringify([accessKey, country, mode]) : null;
  const geography = useMemo(() => {
    // Recreate the metadata cache after access changes or explicit retries.
    if (!snapshot) return null;
    return createGeography(snapshot);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey, snapshot, refresh]);
  const ownedScope = scopeState?.key === geographyKey ? scopeState : null;
  const needsValidation = !!(snapshot && ownedScope?.value?.kind === 'admin' && ownedScope.boundary !== snapshot.boundary_revision_id);
  const scope = ownedScope && snapshot && !needsValidation ? ownedScope.value : mode === 'me' && can('view_my_territory') ? { kind: 'me' } : null;
  const queryKey = active && snapshot && scope ? JSON.stringify([accessKey, snapshot.snapshot_id, scope, refresh, excludedClassKeys]) : null;
  const mapQueryKey = queryKey ? JSON.stringify([queryKey, classCode]) : null;
  currentMapKey.current = mapQueryKey;
  const stats = useCCResource(queryKey ? JSON.stringify([queryKey, statsRefresh]) : null,
    (signal) => CC.stats(snapshot, scope, permission.access_revision, signal, excludedClassKeys));
  const navigation = navigationState.key === scopeKey ? navigationState : {};
  const validation = useCCResource(needsValidation ? JSON.stringify([scopeKey, refresh, validationRefresh, ownedScope.value.subject_id]) : null,
    (signal) => geography.validatePath(path, signal));
  useEffect(() => {
    navigationRequest.current?.abort();
    return () => navigationRequest.current?.abort();
  }, [scopeKey, active]);
  useEffect(() => {
    if (!needsValidation || !validation.data) return;
    const next = validation.data.at(-1);
    setScopeState({ key: geographyKey, boundary: snapshot.boundary_revision_id, value: next
      ? { kind: 'admin', subject_id: next.id, name: next.name, level: next.level, bounds: next.bounds } : null });
    setPath(validation.data);
    setNavigationState({ key: scopeKey, message: next?.id === ownedScope.value.subject_id ? null : 'ci.geographyAdjusted' });
  }, [needsValidation, validation.data, geographyKey, snapshot, scopeKey, ownedScope]);
  const selectScope = (value, ancestors = []) => {
    navigationRequest.current?.abort();
    setScopeState({ key: geographyKey, boundary: snapshot?.boundary_revision_id, value });
    setPath(ancestors); setPolygon(null); setNavigationState({});
  };
  const navigateRegion = async (region) => {
    if (!geography) return;
    navigationRequest.current?.abort();
    const controller = new AbortController(); navigationRequest.current = controller;
    setNavigationState({ key: scopeKey, loading: true });
    try {
      const ancestors = await geography.resolve(region, ownedScope ? path : [], controller.signal);
      if (controller.signal.aborted) return;
      const node = ancestors.at(-1);
      selectScope({ kind: 'admin', subject_id: node.id, name: node.name, level: node.level, bounds: node.bounds }, ancestors);
    } catch (error) {
      if (!controller.signal.aborted) setNavigationState({ key: scopeKey, error, retry: () => navigateRegion(region) });
    }
  };
  const selectSnapshot = (id) => {
    const next = available.find((item) => item.snapshot_id === id);
    if (!next) return;
    navigationRequest.current?.abort();
    const previous = snapshot || available.find((item) => item.snapshot_id === selection?.previousId) || sidebarRecord?.snapshot;
    // Year changes return Explore to the authorized country root. Keep territory
    // modes scoped, and let boundary validation check this root in the new release.
    const root = ownedScope && path[0]?.level === 'COUNTRY' ? path[0] : null;
    if (mode === 'explore' && previous?.year !== next.year && root) {
      setScopeState({ key: geographyKey, boundary: ownedScope.boundary,
        value: { kind: 'admin', subject_id: root.id, name: root.name, level: root.level, bounds: root.bounds } });
      setPath([root]);
    }
    const crop = previous?.classes.find((entry) => entry.code === classCode);
    // Class numbers can have different meanings in different dictionaries.
    const nextCrop = crop && next.classes.find((entry) => entry.semantic_key === crop.semantic_key && next.present_class_codes.includes(entry.code));
    setExclusionState({ key: exclusionOwner, keys: excludedClassKeys.filter((key) => next.classes.some((entry) => entry.semantic_key === key && next.present_class_codes.includes(entry.code))) });
    setSelection({ owner, country, id }); setClassCode(nextCrop?.code ?? null); setPolygon(null); setNavigationState({});
    try { localStorage.setItem(CC_SELECTION_KEY, JSON.stringify({ version: 2, owner, country, snapshot_id: id })); } catch (_) { /* Storage can be disabled. */ }
  };
  const selectYear = (year) => {
    const choices = available.filter((item) => item.year === year);
    if (choices.length === 1) { selectSnapshot(choices[0].snapshot_id); return; }
    navigationRequest.current?.abort();
    setSelection({ owner, country, id: null, year, previousId: snapshot?.snapshot_id || selection?.previousId });
    setPolygon(null); setNavigationState({});
  };
  const selectMode = (value) => { setModeState({ key: accessKey, value }); setScopeState(null); setPath([]); setPolygon(null); };
  let status = !active ? 'idle' : !permission ? capabilities.identity === identity && capabilities.status === 'error' ? 'error' : 'loading'
    : publications.status === 'error' ? 'error' : publications.status !== 'ready' ? 'loading'
      : !available.length ? 'unavailable' : !snapshot ? 'selectionUnavailable' : 'ready';
  if (permission && !permission.capabilities.length) status = 'denied';
  if (status === 'selectionUnavailable' && selection?.owner === owner && selection.country === country && selection.id === null) status = 'chooseVersion';
  const controlSnapshot = snapshot || (status === 'chooseVersion' ? available.find((item) => item.snapshot_id === selection?.previousId) : null);
  // Keep a presentation-only copy while another publication loads. Requests and
  // map geometry still use the strict current snapshot/scope above.
  const sidebarScope = scope || ((needsValidation || status === 'chooseVersion') ? ownedScope?.value : null);
  const sidebarKey = active && accessKey && sidebarScope && (snapshot || status === 'chooseVersion')
    ? JSON.stringify([accessKey, country, mode, sidebarScope.kind, sidebarScope.subject_id, sidebarScope.membership_revision, excludedClassKeys]) : null;
  useEffect(() => {
    if (!sidebarKey) { setSidebarRecord(null); return; }
    if (snapshot && stats.status === 'ready') {
      setSidebarRecord({ key: sidebarKey, snapshot, data: stats.data });
    }
  }, [sidebarKey, snapshot, stats.status, stats.data]);
  const retained = sidebarKey && sidebarRecord?.key === sidebarKey ? sidebarRecord : null;
  const sidebar = stats.status === 'ready' && snapshot ? { snapshot, data: stats.data }
    : retained || { snapshot: controlSnapshot, data: null };
  const sidebarPrevious = (!!retained && stats.status !== 'ready') || (!!controlSnapshot && status === 'chooseVersion');
  const mapLoading = mapMounted && mapQueryKey && (mapLoad?.key !== mapQueryKey || mapLoad.loading);
  const loading = active && mode !== 'manage' && (status === 'loading' || (needsValidation && validation.status === 'loading') ||
    navigation.loading || stats.status === 'loading' ||
    mapLoading);
  const metadata = snapshot ? { ...snapshot, classes: Object.fromEntries(snapshot.classes.map((entry) => [entry.code, entry])) } : null;
  return <CCContext.Provider value={{ transport: 'v2', setTransport, active, status,
    error: capabilities.error || publications.error, retry, refresh, identity, accessKey, permission, can,
    country, setCountry: (value) => { setCountry(value.toUpperCase()); setScopeState(null); setPath([]); },
    publications: available, snapshot, dataset: snapshot, metadata,
    selectSnapshot, selectYear, selectionYear: snapshot?.year || (selection?.owner === owner && selection.country === country ? selection.year : undefined),
    mode, selectMode, scope, selectScope, navigateRegion, geography,
    navigation: needsValidation ? { loading: validation.status === 'loading', error: validation.error, retry: retryValidation } : navigation,
    path: ownedScope && !needsValidation && snapshot ? path : [],
    scopeKey, queryKey, mapQueryKey, stats, classCode, setClassCode: (code) => {
      if (code != null && snapshot?.classes.some((entry) => entry.code === code && excludedClassKeys.includes(entry.semantic_key))) return;
      setClassCode(code);
    }, comparisonIds, setComparisonIds,
    exclusionsEnabled, excludedClassKeys, toggleCropExclusion, clearCropExclusions,
    legendKey: snapshot && scope ? JSON.stringify([accessKey, country, snapshot.snapshot_id, scope]) : null,
    sidebar, sidebarPrevious, sidebarScope, controlSnapshot, retryStats, sidebarPath: sidebarKey && ownedScope ? path : [],
    loading: !!loading, registerMap, reportMapLoading,
    legendScope, setLegendScope,
    selectedPolygon: polygon?.key === mapQueryKey && mapQueryKey ? polygon.feature : null,
    setSelectedPolygon: (feature) => setPolygon(feature ? { key: mapQueryKey, feature } : null),
  }}><YieldProvider active={active} identity={identity}>{children}</YieldProvider></CCContext.Provider>;
}
